import { Box, Skeleton, SxProps, Theme } from '@mui/material';
import { ReactNode } from 'react';
import { AppCardTitleSkeleton } from './AppCardTitleSkeleton';

/** 16:9 (or other) aspect-ratio media placeholder. */
export interface AppCardSkeletonMediaAspectRatio {
  aspectRatio: string | number;
}

/** Fixed-height media placeholder (e.g. audio waveform). */
export interface AppCardSkeletonMediaFixedHeight {
  height: string | number;
}

export type AppCardSkeletonMedia =
  | AppCardSkeletonMediaAspectRatio
  | AppCardSkeletonMediaFixedHeight;

const DEFAULT_MEDIA: AppCardSkeletonMediaAspectRatio = { aspectRatio: '16/9' };

function isAspectRatioMedia(media: AppCardSkeletonMedia): media is AppCardSkeletonMediaAspectRatio {
  return 'aspectRatio' in media;
}

interface AppCardSkeletonProps {
  /**
   * Media placeholder shape. Defaults to 16/9.
   * Use `{ height }` for fixed-height media (e.g. audio).
   */
  media?: AppCardSkeletonMedia;
  /** Bottom row (meta chips, actions, etc.). */
  footer?: ReactNode;
  /** Width of the title skeleton block. */
  titleWidth?: string | number;
  sx?: SxProps<Theme>;
}

/**
 * Shared loading placeholder for grid-style cards: bordered shell, media, title, optional footer.
 * Domain skeletons supply only the footer (and media shape when not 16/9).
 */
export function AppCardSkeleton({
  media = DEFAULT_MEDIA,
  footer,
  titleWidth,
  sx,
}: AppCardSkeletonProps) {
  const mediaSx = isAspectRatioMedia(media)
    ? { width: '100%', height: 'auto' as const, aspectRatio: media.aspectRatio }
    : { width: '100%', height: media.height };

  return (
    <Box
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        borderRadius: 1,
        overflow: 'hidden',
        border: '1px solid',
        borderColor: 'divider',
        ...sx,
      }}
    >
      <Skeleton variant="rectangular" sx={mediaSx} />
      <Box sx={{ p: 1.5 }}>
        <AppCardTitleSkeleton width={titleWidth} />
      </Box>
      {footer}
    </Box>
  );
}
